import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { toast } from 'react-toastify';

import { ModalTypes } from 'config';
import { EDeliveryStatus } from 'enums';
import Shapes from 'shapes/main';
import OtoSpinner from '../common/OtoSpinner';
import DeliveryTile from './DeliveryItem/DeliveryTile';
import withSetModal, { TSetModalFunction } from '../../containers/WithSetModal';
import GroupedFlow from '../common/GroupedFlow';
import { createDeliveriesFlow } from './RestaurateurDeliveriesList';
import DriverDeliveryButtons from './DriverDeliveryButtons';
import Delivery from 'utils/deliveries';
import type { IDelivery, IOrder, User } from 'types';

import './DriverDeliveriesList.scss';

type DriverDeliveriesListProps = {
  deliveries: IDelivery[];
  loading: boolean;
  onOrderOrderPrepareTimeSet: (
    delivery: IDelivery,
    prepareTime: number
  ) => void;
  onDeliveryUpdate: (
    delivery: IDelivery,
    payload: Partial<IDelivery>
  ) => Promise<void>;
  setModal: TSetModalFunction;
  user: User;
};

export const DriverDeliveriesList: React.FC<DriverDeliveriesListProps> = (
  props
) => {
  const {
    deliveries,
    loading,
    onOrderOrderPrepareTimeSet,
    onDeliveryUpdate,
    setModal,
    user,
  } = props;
  const [loadingDeliveries, setLoadingDeliveries] = useState({});

  const updateDelivery = (delivery, payload) => {
    setLoadingDeliveries({ ...loadingDeliveries, [delivery.id]: true });
    onDeliveryUpdate(delivery, payload).finally(() =>
      setLoadingDeliveries({ ...loadingDeliveries, [delivery.id]: false })
    );
  };

  const changeStatus = (delivery: IDelivery, status: EDeliveryStatus) =>
    updateDelivery(delivery, { status });

  const handlePhoneClick = (delivery: IDelivery) => {
    if (
      [EDeliveryStatus.NEW, EDeliveryStatus.ASSIGNED].includes(delivery.status)
    ) {
      changeStatus(delivery, EDeliveryStatus.ARRIVING);
    }
  };

  const proposeOtherTime = (delivery: IDelivery & { order?: IOrder }) => {
    setModal(
      {
        confirm: ({ prepare_time, arriving_in }, closeModal) => {
          closeModal();
          if (!!delivery.order_id && !!prepare_time) {
            onOrderOrderPrepareTimeSet(delivery, prepare_time);
          }
          return updateDelivery(delivery, {
            status: EDeliveryStatus.ASSIGNED,
            arriving_in,
          });
        },
        modalSpecificProps: {
          delivery,
          order: delivery?.order,
        },
      },
      ModalTypes.ACCEPT_DELIVERY
    );
  };

  const requestDelay = (delivery: IDelivery) => {
    setModal(
      {
        title: i18n.t('driver-deliveries-list.request-delay-title'),
        text: (
          <>
            <div>
              {i18n.t('driver-deliveries-list.request-delay-selected', { minutes: delivery.arriving_in })}
            </div>
            <div>
              {i18n.t('driver-deliveries-list.request-delay-remaining', { minutes: Delivery.getDriverTimeToArrive(delivery) })}
            </div>
          </>
        ),
        options: [
          ...[5, 10, 15, 20, 25, 30].map((num) => ({
            value: num,
            label: `${num} min`,
          })),
        ],
        confirm: (selectedTime, customTime) => {
          if (!delivery.arriving_in) {
            toast.error(
              i18n.t('driver-deliveries-list.error-cannot-change-time')
            );
            return;
          }
          return updateDelivery(delivery, {
            arriving_in:
              delivery.arriving_in + parseInt(customTime || selectedTime, 10),
          });
        },
        confirmColor: 'info',
        confirmText: i18n.t('driver-deliveries-list.add-time'),
      },
      ModalTypes.SELECT
    );
  };

  const deliveriesFlow = createDeliveriesFlow('driver-view');

  const canBeEdited = (delivery: IDelivery) =>
    !delivery.assigned_to || delivery.assigned_to === user.id;
  const isLoading = (delivery: IDelivery) => !!loadingDeliveries[delivery.id];

  const renderDeliveryItem = (delivery: IDelivery) => (
    <DeliveryTile
      key={delivery.id}
      delivery={delivery}
      isDriver={true}
      onPhoneClick={handlePhoneClick}
    >
      {isLoading(delivery) && <OtoSpinner />}
      {!isLoading(delivery) && canBeEdited(delivery) && (
        <DriverDeliveryButtons
          changeStatus={changeStatus}
          delivery={delivery}
          proposeOtherTime={proposeOtherTime}
          requestDelay={requestDelay}
        />
      )}
    </DeliveryTile>
  );

  return (
    <>
      <div className="row">
        {!!loading && <OtoSpinner center />}
        {deliveries.length ? (
          <GroupedFlow
            fieldKey="status"
            data={deliveries}
            flow={deliveriesFlow}
            emptyText={i18n.t('driver-deliveries-list.empty-category')}
            renderItem={renderDeliveryItem}
            rowClassName="d-flex flex-row flex-wrap"
          />
        ) : (
          i18n.t('delivery.driver-view.no-deliveries')
        )}
      </div>
    </>
  );
};

DriverDeliveriesList.propTypes = {
  deliveries: PropTypes.arrayOf(Shapes.deliveryShape.isRequired).isRequired,
  loading: PropTypes.bool.isRequired,
  onDeliveryUpdate: PropTypes.func.isRequired,
  onOrderOrderPrepareTimeSet: PropTypes.func.isRequired,
};

export default withSetModal(DriverDeliveriesList);
